import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { getEmailAnalytics, getEmailTrends, getEmailPerformance } from '../services/emailAnalyticsService';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const EmailAnalyticsDashboard = () => {
  const [timeRange, setTimeRange] = useState('30days');
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [analyticsData, trendsData, performanceData] = await Promise.all([
          getEmailAnalytics(timeRange),
          getEmailTrends(30),
          getEmailPerformance(),
        ]);
        setAnalytics(analyticsData);
        setTrends(trendsData);
        setPerformance(performanceData);
      } catch (error) {
        console.error('Error fetching email analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeRange]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const emailTypeData = Object.entries(analytics.emailTypes).map(([name, value]) => ({
    name,
    value,
  }));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Email Analytics Dashboard</Typography>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            label="Time Range"
          >
            <MenuItem value="7days">Last 7 Days</MenuItem>
            <MenuItem value="30days">Last 30 Days</MenuItem>
            <MenuItem value="90days">Last 90 Days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Total Emails Sent</Typography>
            <Typography variant="h4">{analytics.totalEmailsSent}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Average Delivery Rate</Typography>
            <Typography variant="h4">
              {Object.values(analytics.deliveryRates).reduce((a, b) => a + b, 0) / 
               Object.keys(analytics.deliveryRates).length || 0}%
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Average Open Rate</Typography>
            <Typography variant="h4">
              {Object.values(analytics.openRates).reduce((a, b) => a + b, 0) / 
               Object.keys(analytics.openRates).length || 0}%
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Average Click Rate</Typography>
            <Typography variant="h4">
              {Object.values(analytics.clickRates).reduce((a, b) => a + b, 0) / 
               Object.keys(analytics.clickRates).length || 0}%
            </Typography>
          </Paper>
        </Grid>

        {/* Trends Chart */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Email Trends
            </Typography>
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="sent" stroke="#8884d8" name="Sent" />
                  <Line type="monotone" dataKey="delivered" stroke="#82ca9d" name="Delivered" />
                  <Line type="monotone" dataKey="opened" stroke="#ffc658" name="Opened" />
                  <Line type="monotone" dataKey="clicked" stroke="#ff7300" name="Clicked" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Email Type Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Email Type Distribution
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={emailTypeData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label
                  >
                    {emailTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Performance Table */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Email Performance by Type
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email Type</TableCell>
                    <TableCell align="right">Sent</TableCell>
                    <TableCell align="right">Delivery Rate</TableCell>
                    <TableCell align="right">Open Rate</TableCell>
                    <TableCell align="right">Click Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {performance.map((item) => (
                    <TableRow key={item.emailType}>
                      <TableCell>{item.emailType}</TableCell>
                      <TableCell align="right">{item.sent}</TableCell>
                      <TableCell align="right">{item.deliveryRate.toFixed(1)}%</TableCell>
                      <TableCell align="right">{item.openRate.toFixed(1)}%</TableCell>
                      <TableCell align="right">{item.clickRate.toFixed(1)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EmailAnalyticsDashboard; 